import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Headset,
  PhoneCall,
} from "lucide-react";
import { ghostButtonClass } from "../components/ui";
import { request } from "../lib/api";
import { getRole } from "../lib/auth";
import { canCallEmergency, emergencyDialNumber, emergencyPhoneNumber } from "../lib/helpline";
import { landingPathFor } from "../lib/landing";
import { supportDate, type SupportTicket } from "../support/types";

const teacherQuestions = ["teacherAccess", "teacherComplete", "teacherSummary"];
const facilitatorQuestions = [
  "facilitatorAssigned",
  "facilitatorAttendance",
  "facilitatorProgress",
  "facilitatorEndNote",
];
const sharedQuestions = ["password", "language"];

export function Helpline() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const role = getRole();
  const facilitator = role === "facilitator";
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsError, setTicketsError] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState(false);

  useEffect(() => {
    request<{ tickets: SupportTicket[]; next_offset: number | null }>("/api/support/tickets")
      .then((data) => {
        setTickets(data.tickets);
        setNextOffset(data.next_offset);
      })
      .catch(() => setTicketsError(true))
      .finally(() => setTicketsLoading(false));
  }, []);

  async function loadMore() {
    if (nextOffset === null || moreLoading) return;
    setMoreLoading(true);
    setMoreError(false);
    try {
      const data = await request<{ tickets: SupportTicket[]; next_offset: number | null }>(
        `/api/support/tickets?offset=${nextOffset}`
      );
      setTickets((current) => [...current, ...data.tickets]);
      setNextOffset(data.next_offset);
    } catch {
      setMoreError(true);
    } finally {
      setMoreLoading(false);
    }
  }

  const questionKeys = [
    ...(facilitator ? facilitatorQuestions : teacherQuestions),
    ...sharedQuestions,
  ];

  return (
    <div className="flex flex-col gap-6 pb-8">
      {role !== "teacher" && (
        <NavLink to={landingPathFor(role ?? undefined)} className={ghostButtonClass}>
          <ChevronLeft size={16} />
          {t("backToHome")}
        </NavLink>
      )}

      <section className="rounded-2xl bg-accent-brand p-5 text-content-on-accent shadow-raised">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold">
          <Headset size={13} />
          {t(facilitator ? "helpFacilitator" : "helpTeacher")}
        </span>
        <h1 className="mt-4 text-2xl font-bold tracking-tight">{t("helpTitle")}</h1>
        <p className="mt-1 text-sm leading-6 text-white/85">{t("helpIntro")}</p>
      </section>

      <section className="rounded-2xl bg-surface p-4 shadow-raised">
        <h2 className="text-base font-semibold text-app-ink">{t("helpEmergencyTitle")}</h2>
        {/* The number is on the call button; only say when there isn't one yet. */}
        {!canCallEmergency && (
          <p className="mt-1 text-sm leading-6 text-app-muted">{t("helpEmergencyNumberPending")}</p>
        )}
        {canCallEmergency ? (
          <a
            href={`tel:${emergencyDialNumber}`}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-strong px-4 py-3 text-base font-semibold text-white shadow-raised hover:bg-accent-strong-hover"
            aria-label={`${t("helpEmergencyCall")} ${emergencyPhoneNumber}`}
          >
            <PhoneCall size={19} />
            {t("helpEmergencyCall")}
            <span dir="ltr">{emergencyPhoneNumber}</span>
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-strong px-4 py-3 text-base font-semibold text-white shadow-raised disabled:cursor-not-allowed"
          >
            <PhoneCall size={19} />
            {t("helpEmergencyCall")}
          </button>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-app-ink">{t("helpCommonQuestions")}</h2>
        <div className="flex flex-col gap-2">
          {questionKeys.map((key) => (
            <details key={key} className="group rounded-xl bg-surface shadow-raised">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3.5 text-sm font-semibold text-app-ink [&::-webkit-details-marker]:hidden">
                <span className="flex-1">{t(`helpFaq_${key}_q`)}</span>
                <ChevronDown size={16} className="shrink-0 text-app-muted group-open:rotate-180" />
              </summary>
              <p className="border-t border-app-line px-4 py-3 text-sm leading-6 text-app-copy">
                {t(`helpFaq_${key}_a`)}
              </p>
            </details>
          ))}
        </div>
      </section>

      <section className="rounded-2xl bg-surface p-4 shadow-raised">
        <h2 className="text-base font-semibold text-app-ink">{t("helpStillNeedHelp")}</h2>
        <p className="mt-1 text-sm leading-6 text-app-muted">{t("helpTicketIntro")}</p>
        <NavLink
          to="/helpline/new"
          state={location.state}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-content-on-accent hover:bg-accent-hover"
        >
          {t("helpCreateTicket")}
          <ArrowUpRight size={16} />
        </NavLink>
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-app-ink">{t("helpYourRequests")}</h2>
        {ticketsLoading ? (
          <p className="text-sm text-app-muted">{t("loading")}</p>
        ) : ticketsError ? (
          <p className="text-sm text-accent-strong">{t("helpTicketsUnavailable")}</p>
        ) : tickets.length === 0 ? (
          <p className="rounded-xl bg-surface p-4 text-sm text-app-muted shadow-raised">
            {t("helpNoRequests")}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {tickets.map((ticket) => (
              <NavLink
                key={ticket.id}
                to={`/helpline/tickets/${ticket.id}`}
                state={location.state}
                className="flex items-center gap-3 rounded-xl bg-surface p-4 shadow-raised"
              >
                <span className="min-w-0 flex-1">
                  <strong className="block truncate text-sm text-app-ink">{ticket.subject}</strong>
                  <span className="mt-1 block text-xs text-app-muted">
                    {supportDate(ticket.updated_at, i18n.language)} ·{" "}
                    {t(`helpStatus_${ticket.status}`)}
                  </span>
                </span>
                <ChevronRight size={17} className="text-app-muted rtl:rotate-180" />
              </NavLink>
            ))}
            {moreError && (
              <p role="alert" className="text-sm text-accent-strong">
                {t("helpTicketsUnavailable")}
              </p>
            )}
            {nextOffset !== null && (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={moreLoading}
                className="rounded-xl border border-app-line bg-surface px-4 py-3 text-sm font-semibold text-accent disabled:opacity-50"
              >
                {moreLoading ? t("loading") : t("helpLoadMore")}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
